import { createFileRoute, retainSearchParams } from "@tanstack/react-router";
import { type ReactNode, useMemo } from "react";
import ChatView from "../components/ChatView";
import { BrowserPreviewPanel } from "../components/BrowserPreviewPanel";
import { resolveBrowserSurfaceLabel } from "../components/browserSurfaceLabel";
import { resolveRightPanelResourceLabels } from "../components/rightPanelResourceLabels";
import { RightSidebarShell } from "../components/RightSidebarShell";
import {
  RightPanelInlineSidebar,
  RightPanelSheet,
} from "../components/ThreadRightPanelRouteLayout";
import {
  RIGHT_PANEL_FULLSCREEN_MEDIA_QUERY,
  RIGHT_PANEL_OVERLAY_MEDIA_QUERY,
} from "../components/rightPanelLayout";
import { draftRightPanelKey } from "../rightPanelStore";
import { useRightPanelState, useSeedRightPanelFromLink } from "../useRightPanel";
import { resolveRightPanelRenderState } from "./rightPanelRenderState";
import type { RightPanelKind } from "../rightPanelSurface";
import { DraftId } from "../draftId";
import { SidebarInset } from "../components/ui/sidebar";
import { type DiffRouteSearch, type RightPanelId, parseDiffRouteSearch } from "../diffRouteSearch";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { selectBrowserThreadState, useBrowserStateStore } from "../browserStateStore";
import {
  filterDraftPreviewTabs,
  resolveDraftPreviewPanel,
  shouldRenderDraftRightPanelSurface,
} from "./-draftChatThreadRoute.logic";
import { useObserveActiveRouteCloudRuntimeConnection } from "./ensureActiveRouteCloudRuntime";
import { useDraftThreadIdentity } from "./-useDraftThreadIdentity";
import { useDraftRightPanelControls } from "./-useDraftRightPanelControls";
import { useDraftRouteRedirects } from "./-useDraftRouteRedirects";

const RIGHT_PANEL_OVERLAY_QUERY = RIGHT_PANEL_OVERLAY_MEDIA_QUERY;

function DraftChatThreadRouteView() {
  const search = Route.useSearch();
  const { draftId: rawDraftId } = Route.useParams();
  const draftId = DraftId.make(rawDraftId);
  const { draftSession, canonicalThreadRef, threadRef } = useDraftThreadIdentity(draftId);
  useObserveActiveRouteCloudRuntimeConnection(draftSession?.environmentId);
  const { rightPanel, previewTabId } = useDraftPanelSearch(search);
  const browserThreadState = useBrowserStateStore((state) =>
    selectBrowserThreadState(state.browserStateByThreadKey, threadRef),
  );
  // A draft has no thread id, so its panel is keyed separately and never
  // persisted — restoring tabs onto a draft that no longer exists is worse
  // than opening clean.
  const draftPanelKey = draftRightPanelKey(draftId);
  // Seeded against the draft's own key, not the thread key. `ChatView` seeds
  // from `activeThreadKey`, which a draft does not have until it is promoted —
  // so a shared `?rightPanel=preview` link landed on a key nothing on this
  // route reads, and the panel stayed shut.
  useSeedRightPanelFromLink(draftPanelKey, search as unknown as Record<string, unknown>);
  const draftPanelState = useRightPanelState(draftPanelKey);
  // Same invariant as the thread route: the store decides whether the panel is
  // showing. Deriving this from the search params left every toggle writing
  // state nothing read.
  const { rightSidebarOpen, hasSidebarState: rightSidebarHasState } = resolveRightPanelRenderState(
    draftPanelState,
    search,
  );
  const browserSidebarLabel = useMemo(
    () => resolveBrowserSurfaceLabel({ browserThreadState, previewTabId }),
    [browserThreadState, previewTabId],
  );
  // Keyed by surface id: a draft can host several browser sessions, and only
  // the one the label resolver describes gets titled.
  const rightSidebarResourceLabels = useMemo(
    () =>
      resolveRightPanelResourceLabels({
        surfaces: draftPanelState.surfaces,
        activeSurfaceId: draftPanelState.activeSurfaceId,
        activeLabel: browserSidebarLabel,
      }),
    [browserSidebarLabel, draftPanelState.activeSurfaceId, draftPanelState.surfaces],
  );
  const shouldUseRightPanelSheet = useMediaQuery(RIGHT_PANEL_OVERLAY_QUERY);
  const shouldUseRightPanelFullscreen = useMediaQuery(RIGHT_PANEL_FULLSCREEN_MEDIA_QUERY);
  // A draft has no thread id, so its panel is keyed separately and never
  // persisted — restoring tabs onto a draft that no longer exists is worse
  // than opening clean.
  const {
    close: closeRightPanel,
    select: selectRightPanel,
    activateSurface: activateRightPanelSurface,
    closeSurface: closeRightPanelSurfaceTab,
  } = useDraftRightPanelControls(draftPanelKey);

  useDraftRouteRedirects({ canonicalThreadRef, hasDraftSession: Boolean(draftSession) });

  if (canonicalThreadRef) {
    return (
      <SidebarInset className="h-full min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
        <ChatView
          environmentId={canonicalThreadRef.environmentId}
          threadId={canonicalThreadRef.threadId}
          routeKind="server"
        />
      </SidebarInset>
    );
  }

  if (!draftSession) {
    return null;
  }

  const draftSurface = (
    <DraftPanelSurface
      hasSidebarState={rightSidebarHasState}
      sidebarOpen={rightSidebarOpen}
      panelState={draftPanelState}
      resourceLabels={rightSidebarResourceLabels}
      onSelectSurface={activateRightPanelSurface}
      onAddSurface={selectRightPanel}
      onCloseSurface={closeRightPanelSurfaceTab}
      onClose={closeRightPanel}
    >
      <DraftActivePane
        rightPanel={rightPanel}
        draftSession={draftSession}
        previewTabId={previewTabId}
      />
    </DraftPanelSurface>
  );

  return (
    <DraftRouteSurface
      chat={
        <ChatView
          draftId={draftId}
          environmentId={draftSession.environmentId}
          threadId={draftSession.threadId}
          showRightPanelLauncher={!rightSidebarOpen}
          routeKind="draft"
        />
      }
      surface={draftSurface}
      hasSurface={shouldRenderDraftRightPanelSurface({
        hasSidebarState: rightSidebarHasState,
        sidebarOpen: rightSidebarOpen,
      })}
      open={rightSidebarOpen}
      asSheet={shouldUseRightPanelSheet}
      fullscreen={shouldUseRightPanelFullscreen}
      onClose={closeRightPanel}
    />
  );
}

/**
 * The draft's two layouts, which differ only in the chrome around the panel.
 *
 * A wide window docks it beside the transcript; a narrow one slides it over.
 * Written as two returns, the transcript half was duplicated and the panel had
 * two places to be wrapped — so a prop added to one was silently missing from
 * the other.
 */
function DraftRouteSurface({
  chat,
  surface,
  hasSurface,
  open,
  asSheet,
  fullscreen,
  onClose,
}: {
  readonly chat: ReactNode;
  readonly surface: ReactNode;
  /**
   * Whether there is a panel at all.
   *
   * Asked explicitly rather than by testing `surface` for truthiness: `surface`
   * is an element whose *render* may be empty, so it is always truthy and the
   * sheet would open over an empty panel.
   */
  readonly hasSurface: boolean;
  readonly open: boolean;
  readonly asSheet: boolean;
  readonly fullscreen: boolean;
  readonly onClose: () => void;
}) {
  return (
    <>
      <SidebarInset className="h-full min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
        {chat}
      </SidebarInset>
      {asSheet ? (
        hasSurface ? (
          <RightPanelSheet open={open} fullscreen={fullscreen} onClose={onClose}>
            {surface}
          </RightPanelSheet>
        ) : null
      ) : (
        <RightPanelInlineSidebar open={open} onClose={onClose}>
          {surface}
        </RightPanelInlineSidebar>
      )}
    </>
  );
}

export const Route = createFileRoute("/_chat/draft/$draftId")({
  validateSearch: (search) => parseDiffRouteSearch(search),
  search: {
    middlewares: [
      retainSearchParams<DiffRouteSearch>([
        "rightPanelOpen",
        "rightPanelTabs",
        "rightPanel",
        "previewTabId",
      ]),
    ],
  },
  component: DraftChatThreadRouteView,
});

/**
 * The one surface a draft can host.
 *
 * Browser only: every other surface needs a persisted server thread, which a
 * draft does not have. Its own component so the route reads as "render the
 * pane" rather than carrying the which-pane question through the layout.
 */
function DraftActivePane({
  rightPanel,
  draftSession,
  previewTabId,
}: {
  readonly rightPanel: RightPanelId | null;
  readonly draftSession: NonNullable<ReturnType<typeof useDraftThreadIdentity>["draftSession"]>;
  readonly previewTabId: string | undefined;
}) {
  if (rightPanel !== "preview") return null;
  return (
    <BrowserPreviewPanel
      environmentId={draftSession.environmentId}
      threadId={draftSession.threadId}
      projectId={draftSession.projectId}
      threadWorktreePath={draftSession.worktreePath}
      {...(previewTabId ? { initialTabId: previewTabId } : {})}
    />
  );
}

/**
 * The panel itself, or nothing when the route has no panel state to show.
 *
 * The emptiness check lives here rather than at both layout sites: a docked
 * panel and an overlaid one ask the same question, and asking it twice is how
 * one of them ended up answering differently.
 */
function DraftPanelSurface({
  hasSidebarState,
  sidebarOpen,
  panelState,
  resourceLabels,
  onSelectSurface,
  onAddSurface,
  onCloseSurface,
  onClose,
  children,
}: {
  readonly hasSidebarState: boolean;
  readonly sidebarOpen: boolean;
  readonly panelState: ReturnType<typeof useRightPanelState>;
  readonly resourceLabels: Readonly<Record<string, string>>;
  readonly onSelectSurface: (surfaceId: string) => void;
  readonly onAddSurface: (kind: RightPanelKind) => void;
  readonly onCloseSurface: (surfaceId: string) => void;
  readonly onClose: () => void;
  readonly children: ReactNode;
}) {
  if (!shouldRenderDraftRightPanelSurface({ hasSidebarState, sidebarOpen })) return null;
  return (
    <RightSidebarShell
      surfaces={panelState.surfaces}
      activeSurfaceId={panelState.activeSurfaceId}
      isServerThread={false}
      resourceLabels={resourceLabels}
      onSelectSurface={onSelectSurface}
      onAddSurface={onAddSurface}
      onCloseSurface={onCloseSurface}
      onClose={onClose}
    >
      {children}
    </RightSidebarShell>
  );
}

/**
 * The panel a shared link is asking a draft for.
 *
 * A draft can only host the browser, so a link naming any other surface
 * resolves to none rather than to a surface the route cannot render — which is
 * what `resolveDraftPreviewPanel` and `filterDraftPreviewTabs` are for. Read
 * together here because they are one question asked of one search.
 */
function useDraftPanelSearch(search: DiffRouteSearch) {
  const rightPanel = resolveDraftPreviewPanel({ requestedPanel: search.rightPanel ?? null });
  const openRightPanelTabs = useMemo(
    () => filterDraftPreviewTabs(search.rightPanelTabs, rightPanel),
    [rightPanel, search.rightPanelTabs],
  );
  return { rightPanel, openRightPanelTabs, previewTabId: search.previewTabId };
}
